import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Notification {
  id: string;
  titulo: string;
  mensagem: string;
  tipo: 'success' | 'warning' | 'info' | 'error';
  lida: boolean;
  criado_em: string;
  data_leitura?: string;
  usuario_id: string;
  atualizado_em: string;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Buscar notificações do usuário
  const fetchNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('notificacoes')
        .select('*')
        .eq('usuario_id', (await supabase.auth.getUser()).data.user?.id)
        .order('criado_em', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Filtrar e mapear os dados para garantir tipos corretos
      const typedNotifications = (data || []).map(notification => ({
        ...notification,
        tipo: ['success', 'warning', 'info', 'error'].includes(notification.tipo) 
          ? notification.tipo as 'success' | 'warning' | 'info' | 'error'
          : 'info' as const
      }));
      
      setNotifications(typedNotifications);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as notificações",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Marcar notificação como lida
  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notificacoes')
        .update({ 
          lida: true, 
          data_leitura: new Date().toISOString() 
        })
        .eq('id', id);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, lida: true, data_leitura: new Date().toISOString() }
            : notification
        )
      );
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  // Marcar todas como lidas
  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications
        .filter(n => !n.lida)
        .map(n => n.id);

      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from('notificacoes')
        .update({ 
          lida: true, 
          data_leitura: new Date().toISOString() 
        })
        .in('id', unreadIds);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          lida: true, 
          data_leitura: new Date().toISOString() 
        }))
      );
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  // Configurar real-time subscription
  useEffect(() => {
    let currentUserId: string | undefined;
    
    const setupSubscription = async () => {
      fetchNotifications();
      
      const { data: { user } } = await supabase.auth.getUser();
      currentUserId = user?.id;

      if (!currentUserId) return;

      const channel = supabase
        .channel(`notificacoes-changes-${currentUserId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notificacoes',
            filter: `usuario_id=eq.${currentUserId}`
          },
          (payload) => {
            console.log('Mudança nas notificações:', payload);
            
            // Verificar se a notificação é do usuário atual
            const newNotification = payload.new as Notification;
            const oldNotification = payload.old as Notification;
            
            if (payload.eventType === 'INSERT' && newNotification?.usuario_id === currentUserId) {
              setNotifications(prev => [newNotification, ...prev]);
            } else if (payload.eventType === 'UPDATE' && newNotification?.usuario_id === currentUserId) {
              setNotifications(prev =>
                prev.map(n => 
                  n.id === newNotification.id ? newNotification : n
                )
              );
            } else if (payload.eventType === 'DELETE' && oldNotification?.usuario_id === currentUserId) {
              setNotifications(prev =>
                prev.filter(n => n.id !== oldNotification.id)
              );
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const cleanup = setupSubscription();
    
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, []);

  const unreadCount = notifications.filter(n => !n.lida).length;

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
}